import CloseIcon from "@mui/icons-material/Close";
import EmailIcon from "@mui/icons-material/Email";
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import {
  Button,
  Field,
  FieldsContainer,
  globalMessageHandler,
  PasswordField,
  TextField,
} from "common";
import authService from "features/auth/authApi";
import I18n from "i18n";
import { useState } from "react";

export interface ChangeEmailDialogProps {
  open: boolean;
  onClose: () => void;
  currentEmail: string;
}

export default function ChangeEmailDialog({
  open,
  onClose,
  currentEmail,
}: ChangeEmailDialogProps) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const resetForm = () => {
    setCurrentPassword("");
    setNewEmail("");
    setErrors({});
    setSuccess(false);
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!currentPassword) {
      newErrors.current_password = I18n.t(
        "Auth.changeEmail.errors.passwordRequired"
      );
    }
    if (!newEmail) {
      newErrors.new_email = I18n.t("Auth.changeEmail.errors.emailRequired");
    }
    if (newEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      newErrors.new_email = I18n.t("Auth.changeEmail.errors.emailInvalid");
    }
    if (currentEmail === newEmail) {
      newErrors.new_email = I18n.t(
        "Auth.changeEmail.errors.emailMustBeDifferent"
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const {
        success,
        data,
        error: apiError,
      } = await authService.requestEmailChange({
        current_password: currentPassword,
        new_email: newEmail.trim(),
      });

      if (success) {
        setSuccess(true);
        globalMessageHandler.snack({
          message: data?.message || I18n.t("Auth.changeEmail.successMessage"),
          severity: "success",
        });
      } else {
        const errorMessage =
          apiError?.error?.message ||
          I18n.t("Auth.changeEmail.errors.failedToRequest");
        if (apiError?.error?.errors) {
          // Handle validation errors from backend
          setErrors(apiError.error.errors);
        } else {
          setErrors({ general: errorMessage });
        }
        globalMessageHandler.snack({
          message: errorMessage,
          severity: "error",
        });
      }
    } catch (err) {
      const errorMessage = I18n.t("Auth.changeEmail.errors.networkError");
      setErrors({ general: errorMessage });
      globalMessageHandler.snack({
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <EmailIcon />
            {I18n.t("Auth.changeEmail.requestSentTitle")}
          </div>
          <IconButton aria-label="close" onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            {I18n.t("Auth.changeEmail.verificationSent", { email: newEmail })}
          </Alert>

          <div>
            <p>{I18n.t("Auth.changeEmail.checkEmailMessage")}</p>
            <p>
              <strong>{I18n.t("Auth.changeEmail.importantNote")}</strong>{" "}
              {I18n.t("Auth.changeEmail.verifyToComplete")}
            </p>
          </div>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={handleClose} variant="contained">
            {I18n.t("Auth.changeEmail.closeButton")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        component: "form",
        onSubmit: handleSubmit,
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <EmailIcon />
          {I18n.t("Auth.changeEmail.title")}
        </div>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={loading}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {errors.general && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.general}
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 2 }}>
          {I18n.t("Auth.changeEmail.currentEmailLabel", {
            email: currentEmail,
          })}
        </Alert>

        <FieldsContainer>
          <Field fullWidth>
            <PasswordField
              label={I18n.t("Auth.changeEmail.passwordLabel")}
              value={currentPassword}
              onChange={(e: any) => setCurrentPassword(e.target.value)}
              required
              autoComplete="current-password"
              autoFocus
              disabled={loading}
              error={errors.current_password}
              helperText={errors.current_password}
            />
          </Field>
          <Field fullWidth>
            <TextField
              label={I18n.t("Auth.changeEmail.newEmailLabel")}
              type="email"
              value={newEmail}
              onChange={(e: any) => setNewEmail(e.target.value)}
              required
              autoComplete="email"
              disabled={loading}
              error={errors.new_email}
              helperText={errors.new_email}
            />
          </Field>
        </FieldsContainer>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={handleClose} variant="outlined" disabled={loading}>
          {I18n.t("Auth.changeEmail.cancelButton")}
        </Button>
        <Button
          type="submit"
          loading={loading}
          disabled={loading || !currentPassword || !newEmail}
        >
          {I18n.t("Auth.changeEmail.sendVerificationButton")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
